import type { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface DataTablePaginationProps<TData> extends React.ComponentProps<'div'> {
  table: Table<TData>;
  pageSizeOptions?: number[];
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 20, 30, 40, 50],
  className,
  ...props
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation(['table']);
  return (
    <div
      className={cn(
        'flex w-full flex-col-reverse items-center justify-end gap-4 overflow-auto p-1 sm:flex-row sm:gap-8',
        className,
      )}
      {...props}
    >
      <div className='flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8'>
        <div className='flex items-center space-x-2'>
          <p className='whitespace-nowrap font-medium text-sm'>
            {t('rows per page')}
          </p>
          <Select
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
            value={`${table.getState().pagination.pageSize}`}
          >
            <SelectTrigger className='h-8 w-18 data-size:h-8'>
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side='top'>
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex items-center justify-center font-medium text-sm'>
          {t('page')} {table.getState().pagination.pageIndex + 1} {t('of')}{' '}
          {table.getPageCount()}
        </div>
        <div className='flex items-center space-x-2'>
          <Button
            aria-label={t('go to first page')}
            className='hidden size-8 lg:flex'
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.setPageIndex(0)}
            size='icon'
            variant='outline'
          >
            <ChevronsLeft />
          </Button>
          <Button
            aria-label={t('go to previous page')}
            className='size-8'
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
            size='icon'
            variant='outline'
          >
            <ChevronLeft />
          </Button>
          <Button
            aria-label={t('go to next page')}
            className='size-8'
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
            size='icon'
            variant='outline'
          >
            <ChevronRight />
          </Button>
          <Button
            aria-label={t('go to last page')}
            className='hidden size-8 lg:flex'
            disabled={!table.getCanNextPage()}
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            size='icon'
            variant='outline'
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
